import { useMemo } from 'react';
import { makeStyles } from '@material-ui/styles';

import { GlobalLine, Text } from '@bizzabo/design-system';
import { useTimeZones } from '@bizzabo/entities';

import TimePicker from '../../components/TimePicker';
import useTypeTracking from '../../../../analytics';
import { TimeSlot } from '../../types';
import { FOLD_TIMES_AFTER } from '../../constants';
import { useUserSettings } from '../../../../settingsNav';
import { timezoneToAbbrString } from '../../../utils/date';

interface SecondPagePropsType {
    times: Date[];
    timeZone: string;
    selectedTimes: Date[];
    meetingDuration: number; // in milliseconds
    onTimePickerChange: (times: Date[]) => void;
    onTimePickerSelect: (times: Date[]) => void;
    onTimePickerDeselect: (times: Date[]) => void;
    onTimePickerLoadMore: (times: Date[]) => void;
}

const SecondPage: React.FC<SecondPagePropsType> = ({
    times,
    timeZone,
    selectedTimes,
    meetingDuration,
    onTimePickerChange,
    onTimePickerSelect,
    onTimePickerDeselect,
    onTimePickerLoadMore,
}) => {
    const { openScheduledMeetings } = useUserSettings();
    const { trackClickEvent } = useTypeTracking();

    const { data: timeZones } = useTimeZones();
    const tzData = timeZones?.find(({ name }) => name === timeZone);
    const timeZoneLine = tzData
        ? `${tzData.alternativeName} (${timezoneToAbbrString(tzData.name)})`
        : '...';

    const openSettings = () => {
        openScheduledMeetings();
        trackClickEvent({
            subCategory: 'OPEN_MEETING_SETTINGS',
            description: {
                is_received_times: !!times.length,
                location: 'MEETING_WIZARD',
                flow: 'EDIT_SETTINGS',
            },
        });
    };

    const parseTimes: TimeSlot[] = useMemo(() => times.map(time => ({ time })), [times]);

    const classes = useStyles();
    return (
        <>
            <div>
                <Text variant="t50">Preview your availability</Text>
            </div>
            <div className={classes.helpText}>
                <Text variant="t81" className={classes.label}>
                    Here is your mutual availability for meeting based on your{' '}
                    <a onClick={openSettings} className={classes.link}>
                        scheduling preferences
                    </a>
                    .
                </Text>
            </div>
            <div>
                <Text variant="t81" className={classes.timeZoneLine}>
                    <GlobalLine className={classes.timeZoneIcon} /> {timeZoneLine}
                </Text>
            </div>
            <div className={classes.timePicker}>
                <TimePicker
                    times={parseTimes}
                    selectedDates={selectedTimes}
                    duration={meetingDuration}
                    onChange={onTimePickerChange}
                    onSelect={onTimePickerSelect}
                    onDeselect={onTimePickerDeselect}
                    onLoadMore={onTimePickerLoadMore}
                    timeZone={timeZone}
                    showCheckAll
                    foldAfter={FOLD_TIMES_AFTER}
                />
            </div>
        </>
    );
};

const useStyles = makeStyles(
    theme => ({
        label: {
            color: theme.customization.background.G10,
            lineHeight: '18px',
        },
        timePicker: {
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            marginBottom: 16,
        },
        helpText: {
            marginBottom: 4,
        },
        timeZoneLine: {
            display: 'flex',
            alignItems: 'center',
            color: theme.customization.background.G10,
            marginTop: 14,
            marginBottom: 18,
            lineHeight: '15px',
        },
        timeZoneIcon: {
            marginRight: 4,
            width: 15,
            height: 15,
        },
        link: {
            cursor: 'pointer',
            textDecoration: 'underline',
        },
    }),
    { classNamePrefix: 'meetings-scheduling-second-page' }
);

export default SecondPage;
